import { useState, useCallback, useEffect } from 'react';

/**
 * Gestionnaire de navigation pour le PDF dans SmartPDFViewer
 * Gère la navigation, le zoom et les interactions avec le viewer PDF
 */
export const usePDFNavigator = (
  documentUrl, 
  iframeRef, 
  activePassageId = null, 
  passages = [],
  onLoaded = null
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [navigationAttempts, setNavigationAttempts] = useState(0);

  // Construire l'URL du visualiseur PDF.js
  const buildPdfViewerUrl = useCallback((pdfUrl, options = {}) => {
    const viewerPath = '/pdfjs/web/viewer.html';
    
    // Construire l'URL de base
    let url = `${viewerPath}?file=${encodeURIComponent(pdfUrl)}`;
    
    // Ajouter des options supplémentaires
    if (options.search) url += `&search=${encodeURIComponent(options.search)}`;
    if (options.zoom) url += `&zoom=${options.zoom || 'auto'}`;
    if (options.pagemode) url += `&pagemode=${options.pagemode}`;
    if (options.page) url += `&page=${options.page}`;
    
    return url;
  }, []);

  // Naviguer vers une page spécifique du PDF et mettre en surbrillance le texte
  const navigateToPage = useCallback((pageNumber, textToHighlight = null) => {
    if (!iframeRef.current || !pageNumber) return false;
    
    console.log(`Navigation vers la page ${pageNumber}${textToHighlight ? ' avec texte à localiser' : ''}`);
    
    try {
      const iframeWindow = iframeRef.current.contentWindow;
      const pdfViewer = iframeWindow.PDFViewerApplication;
      
      if (pdfViewer && typeof pdfViewer.page !== 'undefined') {
        // D'abord, naviguer vers la page
        pdfViewer.page = pageNumber;
        
        // Si nous avons du texte à mettre en surbrillance
        if (textToHighlight && textToHighlight.length > 5) {
          // Attendre que la page soit complètement rendue
          setTimeout(() => {
            try {
              // Étape 1 : Effectuer la recherche mais garder l'interface invisible
              if (pdfViewer.findController) {
                const searchText = textToHighlight.substring(0, 40); // Limiter la longueur
                
                // Exécuter la recherche avec mise en surbrillance
                pdfViewer.findController.executeCommand('find', {
                  query: searchText,
                  phraseSearch: true,
                  highlightAll: true,
                  findPrevious: false,
                  caseSensitive: false,
                  entireWord: false,
                  matchDiacritics: false
                });
                
                // Attendre que la recherche soit terminée avant de cacher l'interface
                setTimeout(() => {
                  try {
                    // Créer un style pour cacher l'interface de recherche
                    const styleEl = document.createElement('style');
                    styleEl.textContent = `
                      .findbar {
                        opacity: 0 !important;
                        pointer-events: none !important;
                        height: 0 !important;
                        overflow: hidden !important;
                        position: absolute !important;
                        z-index: -1 !important;
                      }
                    `;
                    iframeWindow.document.head.appendChild(styleEl);
                  } catch (e) {
                    console.error("Erreur lors de la modification du CSS:", e);
                    
                    // Méthode alternative : essayer de fermer l'interface
                    if (pdfViewer.findBar) {
                      pdfViewer.findBar.close();
                    }
                  }
                }, 1000); // Attendre une seconde après la recherche
              }
            } catch (e) {
              console.error("Erreur lors de la recherche de texte:", e);
            }
          }, 1000); // Attendre une seconde après la navigation
        }
        
        return true;
      }
      
      // Si PDFViewerApplication n'est pas disponible, essayer des alternatives
      console.log("Navigation standard non disponible, essai d'alternatives...");
      
      // Alternative: recharger l'iframe avec un paramètre de page spécifique
      const newSrc = buildPdfViewerUrl(documentUrl, { page: pageNumber });
      iframeRef.current.src = newSrc;
      return true;
      
    } catch (err) {
      console.error('Erreur lors de la navigation vers la page:', err);
      return false;
    }
  }, [iframeRef, documentUrl]);

  // Gérer le zoom
  const handleZoom = useCallback((newZoomLevel) => {
    if (iframeRef.current) {
      try {
        iframeRef.current.style.transform = `scale(${newZoomLevel / 100})`;
        iframeRef.current.style.transformOrigin = 'center top';
        
        // Ajuster la taille du container pour compenser le zoom
        const container = iframeRef.current.parentElement;
        if (container) {
          container.style.height = `${100 / (newZoomLevel / 100)}%`;
        }
      } catch (err) {
        console.error('Erreur lors de l\'application du zoom:', err);
      }
    }
    setZoomLevel(newZoomLevel);
  }, [iframeRef]);

  // Fonctions de zoom
  const zoomIn = useCallback(() => {
    handleZoom(Math.min(zoomLevel + 10, 200));
  }, [zoomLevel, handleZoom]);

  const zoomOut = useCallback(() => {
    handleZoom(Math.max(zoomLevel - 10, 50));
  }, [zoomLevel, handleZoom]);

  // Surveiller le chargement et la navigation
  useEffect(() => {
    if (!isLoading && activePassageId && navigationAttempts < 3) {
      const activePassage = passages.find(p => p.id === activePassageId);
      if (activePassage && activePassage.pageNumber) {
        console.log(`Tentative de navigation vers la page ${activePassage.pageNumber} (tentative ${navigationAttempts + 1}/3)`);
        setTimeout(() => {
          // Extraire un segment significatif du texte pour la recherche
          let searchText = null;
          if (activePassage.content) {
            const cleanContent = activePassage.content.replace(/\s+/g, ' ').trim();
            if (cleanContent.length > 60) {
              const startIndex = Math.floor(cleanContent.length / 3);
              searchText = cleanContent.substring(startIndex, startIndex + 40);
            } else if (cleanContent.length > 20) {
              searchText = cleanContent.substring(0, Math.min(40, cleanContent.length));
            }
          }
          
          navigateToPage(activePassage.pageNumber, searchText);
          setNavigationAttempts(prev => prev + 1);
        }, 500 * (navigationAttempts + 1)); // Délai progressif entre les tentatives
      }
    }
  }, [isLoading, activePassageId, navigationAttempts, passages, navigateToPage]);

  return {
    isLoading,
    setIsLoading,
    zoomLevel,
    setZoomLevel,
    navigationAttempts,
    setNavigationAttempts,
    buildPdfViewerUrl,
    navigateToPage,
    zoomIn,
    zoomOut,
    handleZoom
  };
};

export default usePDFNavigator;